import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import { AutocompleteCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { ArrowCheckIcon, DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import AddConditionFormTaskType from 'app/pages/loanApplication/tabs/underwriting/tabs/loanConditions/AddConditionFormTaskType';
import {
  getCategoryOptionLabel,
  isCategoryOptionEqual,
} from 'app/pages/loanApplication/tabs/underwritingCDS/Conditions/Conditions.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConditionsTestIds } from '../../../Conditions.testIds';
import { CONDITION_MESSAGE_MAX_LENGTH } from '../../AddConditionsForm.constants';
import {
  conditionCardBodyStyles,
  conditionCardFieldsRowStyles,
  conditionCardHeaderActionsStyles,
  conditionCardHeaderStyles,
  conditionCardHeaderTitleStyles,
  conditionCardStyles,
  taskGridStyles,
} from '../../AddConditionsForm.styles';
import {
  getAvailableTaskTypeOptions,
  getTaskTypeCategoryName,
} from '../../AddConditionsForm.taskUtils';
import TaskCard from '../TaskCard';

import TaskTypeGroupLabel from './components/TaskTypeGroupLabel';
import TaskTypeScopeOption from './components/TaskTypeScopeOption';

const ICON_BUTTON_SIZE = 'small';
const EMPTY_SELECTED_TASK_TYPES = [];

const renderTaskTypeGroup = ({ key, group, children }) => (
  <TaskTypeGroupLabel group={group} key={key}>
    {children}
  </TaskTypeGroupLabel>
);

const ConditionDetailCard = ({
  condition,
  categoryOptions,
  priorToOptions,
  taskTypeDynamicFieldOptions,
  taskTypeOptions,
  validationErrors,
  isDisabledByLock,
  lockedFieldsMap,
  setInForm,
  validateField,
  handleChange,
  onAddConditionTask,
  onAutocompleteChange,
  onCreateNewForm,
  onDelete,
  onRemoveConditionTask,
}) => {
  const {
    conditionId,
    message,
    number,
    priorToType,
    conditionTaskTypes,
    borrowerMessage,
    subCompanyMessage,
    conditionCategory,
    taskTypeIds,
  } = condition;

  const [isExpanded, setIsExpanded] = useState(true);

  const onDeleteClick = useCallback(() => onDelete(conditionId), [conditionId, onDelete]);

  const onToggleExpand = useCallback(() => setIsExpanded(prev => !prev), []);

  const onAddTaskType = useCallback(
    (e, selectedTaskTypes) => {
      const addedTaskType = _.last(selectedTaskTypes);

      if (addedTaskType) {
        onAddConditionTask(conditionId, addedTaskType);
      }
    },
    [conditionId, onAddConditionTask],
  );

  const onRemoveTaskType = useCallback(
    e => onRemoveConditionTask(conditionId, e.currentTarget.dataset.id),
    [conditionId, onRemoveConditionTask],
  );

  const conditionFieldErrors = validationErrors.conditionTaskTypes?.[conditionId];

  const taskTypes = _.values(conditionTaskTypes ?? {});

  const availableTaskTypeOptions = useMemo(
    () =>
      getAvailableTaskTypeOptions({
        taskTypeOptions,
        conditionTaskTypes,
        validTaskTypeIds: taskTypeIds,
      }),
    [taskTypeOptions, conditionTaskTypes, taskTypeIds],
  );

  return (
    <Box sx={conditionCardStyles}>
      <Box sx={conditionCardHeaderStyles}>
        <Typography sx={conditionCardHeaderTitleStyles} variant="medium16">
          {message}
        </Typography>
        <Box sx={conditionCardHeaderActionsStyles}>
          <IconButton
            aria-label="delete condition"
            data-testid={ConditionsTestIds.getConditionDeleteButtonId(conditionId)}
            size={ICON_BUTTON_SIZE}
            onClick={onDeleteClick}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            aria-label={isExpanded ? 'collapse condition' : 'expand condition'}
            data-testid={ConditionsTestIds.getConditionExpandButtonId(conditionId)}
            size={ICON_BUTTON_SIZE}
            onClick={onToggleExpand}
          >
            <ArrowCheckIcon direction={isExpanded ? 'down' : 'right'} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded} unmountOnExit={false}>
        <Box sx={conditionCardBodyStyles}>
          <Box sx={conditionCardFieldsRowStyles}>
            <AutocompleteCDS
              dataTestId={ConditionsTestIds.getConditionCategorySelectId(conditionId)}
              disabled={isDisabledByLock}
              getOptionLabel={getCategoryOptionLabel}
              isHidden={!categoryOptions.length}
              isOptionEqualToValue={isCategoryOptionEqual}
              label="Category"
              name={`conditionTaskTypes.${conditionId}.conditionCategory`}
              options={categoryOptions}
              value={conditionCategory ?? null}
              onChange={onAutocompleteChange}
            />
            <AutocompleteCDS
              disableClearable
              required
              dataTestId={ConditionsTestIds.getConditionPriorToSelectId(conditionId)}
              disabled={isDisabledByLock || lockedFieldsMap.priorToType}
              error={!!conditionFieldErrors?.priorToType}
              helperText={conditionFieldErrors?.priorToType}
              label="Prior To"
              name={`conditionTaskTypes.${conditionId}.priorToType`}
              options={priorToOptions}
              value={priorToType}
              withSortedOptions={false}
              onChange={onAutocompleteChange}
            />
            <TextFieldCDS
              fullWidth
              required
              dataTestId={ConditionsTestIds.getConditionMessageInputId(conditionId)}
              disabled={isDisabledByLock || lockedFieldsMap.message}
              error={!!conditionFieldErrors?.message}
              helperText={conditionFieldErrors?.message}
              label="Condition"
              maxLength={CONDITION_MESSAGE_MAX_LENGTH}
              name={`conditionTaskTypes.${conditionId}.message`}
              value={message ?? ''}
              onChange={handleChange}
            />
            <TextFieldCDS
              disabled
              dataTestId={ConditionsTestIds.getConditionNumberInputId(conditionId)}
              label="Condition Number"
              name={`conditionTaskTypes.${conditionId}.number`}
              value={number ?? ''}
            />
          </Box>

          <TextFieldCDS
            fullWidth
            dataTestId={ConditionsTestIds.getConditionBorrowerMessageInputId(conditionId)}
            disabled={isDisabledByLock || lockedFieldsMap.borrowerMessage}
            error={!!conditionFieldErrors?.borrowerMessage}
            helperText={conditionFieldErrors?.borrowerMessage}
            label="Borrower Facing Condition"
            maxLength={CONDITION_MESSAGE_MAX_LENGTH}
            name={`conditionTaskTypes.${conditionId}.borrowerMessage`}
            value={borrowerMessage ?? ''}
            onChange={handleChange}
          />

          <TextFieldCDS
            fullWidth
            dataTestId={ConditionsTestIds.getConditionSubCompanyMessageInputId(conditionId)}
            disabled={isDisabledByLock || lockedFieldsMap.subCompanyMessage}
            error={!!conditionFieldErrors?.subCompanyMessage}
            helperText={conditionFieldErrors?.subCompanyMessage}
            label="Sub Company Facing Condition"
            maxLength={CONDITION_MESSAGE_MAX_LENGTH}
            name={`conditionTaskTypes.${conditionId}.subCompanyMessage`}
            value={subCompanyMessage ?? ''}
            onChange={handleChange}
          />

          <AutocompleteCDS
            disableCloseOnSelect
            multiple
            OptionContent={TaskTypeScopeOption}
            dataTestId={ConditionsTestIds.getConditionTaskTypeSelectId(conditionId)}
            disabled={isDisabledByLock}
            groupBy={getTaskTypeCategoryName}
            label="Associate Task Type"
            name={`conditionTaskTypes.${conditionId}.taskTypePicker`}
            optionKey="taskTypeId"
            options={availableTaskTypeOptions}
            renderGroup={renderTaskTypeGroup}
            value={EMPTY_SELECTED_TASK_TYPES}
            onChange={onAddTaskType}
          />

          {!!taskTypes.length && (
            <Box sx={taskGridStyles}>
              {taskTypes.map((taskType, index) => (
                <TaskCard
                  headerAction={
                    <Tooltip title="Remove task type">
                      <IconButton
                        aria-label="remove task type"
                        data-id={taskType.taskTypeId}
                        data-testid={ConditionsTestIds.getTaskRemoveButtonId(taskType.taskTypeId)}
                        disabled={isDisabledByLock}
                        size={ICON_BUTTON_SIZE}
                        onClick={onRemoveTaskType}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                  key={taskType.taskTypeId}
                  taskNumber={index + 1}
                  taskTypeName={taskType.name}
                >
                  <AddConditionFormTaskType
                    isInfoButtonHidden
                    isLoanEditor
                    conditionId={conditionId}
                    formName={FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS}
                    handleChange={handleChange}
                    setInForm={setInForm}
                    taskType={taskType}
                    taskTypeDynamicFieldOptions={taskTypeDynamicFieldOptions}
                    validateField={validateField}
                    validationErrors={validationErrors}
                    onCreateNewForm={onCreateNewForm}
                  />
                </TaskCard>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

ConditionDetailCard.propTypes = {
  condition: PropTypes.shape({
    conditionId: PropTypes.string.isRequired,
    message: PropTypes.string,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priorToType: PropTypes.object,
    conditionTaskTypes: PropTypes.object,
    borrowerMessage: PropTypes.string,
    subCompanyMessage: PropTypes.string,
    conditionCategory: PropTypes.object,
    taskTypeIds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  categoryOptions: PropTypes.array.isRequired,
  priorToOptions: PropTypes.array.isRequired,
  taskTypeDynamicFieldOptions: PropTypes.array.isRequired,
  taskTypeOptions: PropTypes.array.isRequired,
  validationErrors: PropTypes.object.isRequired,
  isDisabledByLock: PropTypes.bool.isRequired,
  lockedFieldsMap: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  onAddConditionTask: PropTypes.func.isRequired,
  onAutocompleteChange: PropTypes.func.isRequired,
  onCreateNewForm: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRemoveConditionTask: PropTypes.func.isRequired,
};

export default memo(ConditionDetailCard);
